// Bundle Builder — slice 2.
// State management, click-to-select, quick-add modal interception for
// multi-variant products, batch /cart/add.js with discount code apply,
// and stale-code cleanup. Mirrors the upsell-add-to-cart.js pattern.

import { CartAddEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';

(function () {
  if (customElements.get('bundle-builder')) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const BUNDLE_PROPERTY = '_bundle';

  // Module-level state keyed by section id so it survives Section Rendering API
  // morphs (which destroy and re-create the bundle-builder element).
  const sectionState = new Map();
  function getState(sectionId) {
    let s = sectionState.get(sectionId);
    if (!s) {
      s = { selected: new Map(), discountCode: '' };
      sectionState.set(sectionId, s);
    }
    return s;
  }

  function readDiscountCodes(cart) {
    return Array.isArray(cart.discount_codes)
      ? cart.discount_codes.map(function (d) { return d.code; }).filter(Boolean)
      : [];
  }

  function hasBundleTaggedLine(cart) {
    if (!Array.isArray(cart.items)) return false;
    return cart.items.some(function (item) {
      var props = item.properties;
      return props != null && props[BUNDLE_PROPERTY] === 'true';
    });
  }

  function collectCartSectionIds() {
    return [].slice
      .call(document.querySelectorAll('cart-items-component'))
      .map(function (el) { return el.dataset.sectionId; })
      .filter(Boolean);
  }

  // ---- Stale-code cleanup (runs on every cart:update across the theme) ----
  // We tie cleanup to whatever discount codes have ever been used by a bundle
  // section on this page — collected by scanning live bundle-builder elements.
  var cleanupInFlight = false;
  async function cleanupStaleBundleDiscounts() {
    if (cleanupInFlight) return;
    cleanupInFlight = true;
    try {
      var codes = new Set();
      document.querySelectorAll('bundle-builder[data-discount-code]').forEach(function (el) {
        var c = el.getAttribute('data-discount-code');
        if (c) codes.add(c);
      });
      if (!codes.size) return;

      var cartResp = await fetch(ROOT + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!cartResp.ok) return;
      var cart = await cartResp.json();

      var present = readDiscountCodes(cart);
      var stale = present.filter(function (c) { return codes.has(c); });
      if (!stale.length) return;
      if (hasBundleTaggedLine(cart)) return;

      var remaining = present.filter(function (c) { return !codes.has(c); });
      var sectionIds = collectCartSectionIds();
      await fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          discount: remaining.join(','),
          ...(sectionIds.length && { sections: sectionIds.join(',') }),
        }),
      });
    } catch (e) {
      console.warn('[bundle-builder] cleanup failed:', e);
    } finally {
      cleanupInFlight = false;
    }
  }

  // Hook cleanup once globally — covers cart:update events from any source
  // including bundle adds, PDP adds, drawer line removals.
  if (!window.__bundleBuilderCleanupHooked) {
    window.__bundleBuilderCleanupHooked = true;
    document.addEventListener('cart:update', function () { cleanupStaleBundleDiscounts(); });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cleanupStaleBundleDiscounts, { once: true });
    } else {
      cleanupStaleBundleDiscounts();
    }
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.minProducts = parseInt(this.dataset.minProducts, 10) || 4;
      this.maxProducts = parseInt(this.dataset.maxProducts, 10) || 8;
      if (this.minProducts > this.maxProducts) this.minProducts = this.maxProducts;
      this.discountCode = this.dataset.discountCode || '';
      this.discountLabel = this.dataset.discountLabel || '';
      this.discountPercent = parseFloat(this.dataset.discountPercent) || 0;
      // Money formatting is derived from two Liquid-rendered probes that go
      // through the theme's `| money` filter — so the output is byte-identical
      // to what Shopify renders elsewhere (cart drawer, PDP, etc.). We extract
      // the prefix, suffix, separators, and decimal places from those samples.
      this.moneyFormatSpec = this.parseMoneyFormat(
        this.dataset.moneyZero || '',
        this.dataset.moneySample || ''
      );

      // Capture copy templates from data attributes (set via injected JSON below).
      // We pull these from hidden inline JSON written by the section so we don't
      // have to re-render copy in Liquid for every state.
      this.copyTemplates = this.readCopyTemplates();

      this.state = getState(this.sectionId);
      this.state.discountCode = this.discountCode;

      this.handleClick = this.handleClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleQtyChange = this.handleQtyChange.bind(this);
      this.handleQtyWrapClick = this.handleQtyWrapClick.bind(this);

      // Capture phase so we beat declarative on:click handlers attached
      // directly to descendants (e.g. .quick-add__button--choose). Lets us
      // cancel the modal opening when the user clicks the choose button on an
      // already-selected card (to deselect).
      this.addEventListener('click', this.handleClick, true);

      // Modal CTA interception: capture-phase click on the quick-add modal's
      // add-to-cart button while a bundle card is pending. This beats
      // product-form-component's submit handler at the source by cancelling the
      // submit-button click's default action (= form submit).
      document.addEventListener('click', this.handleDocumentClick, true);

      // Quantity selector wiring (single-variant cards only).
      // The theme's quantity-selector-component dispatches
      // QuantitySelectorUpdateEvent (event name lives on ThemeEvents) on
      // every change. We listen on the section root so we catch events from
      // any selected card.
      this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQtyChange);

      // Stop clicks inside the qty wrapper from reaching the card-level
      // toggle handlers (bare-card click → toggle selection / add to bundle).
      this.addEventListener('click', this.handleQtyWrapClick, true);

      // Quick-add modal CTA label is overridden via CSS when the dialog has
      // the `is-bundle-context` class. We add that class when a bundle card
      // launches the modal; we don't need to clear it on manual close because
      // (a) on the bundle page, the modal is only ever opened from bundle
      // cards, and (b) the class only affects the modal's CTA label.
      this.modalDialog = document.getElementById('quick-add-dialog');

      this.applySelectionState();
      this.renderProgress();
      this.relabelCardButtons();

      // Hide the sticky CTA while the cart drawer dialog is open so it doesn't
      // sit on top of the drawer. Mirrors the listener pattern in cart-drawer.js.
      this.handleDialogOpen = this.handleDialogOpen.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      document.addEventListener(DialogOpenEvent.eventName, this.handleDialogOpen);
      document.addEventListener(DialogCloseEvent.eventName, this.handleDialogClose);

      // Expose CTA height as a CSS var on <body> so the section can reserve
      // bottom padding when the CTA is visible.
      this.updateCtaOffset = this.updateCtaOffset.bind(this);
      this.ctaResizeObserver = new ResizeObserver(this.updateCtaOffset);
      var ctaWrap = this.querySelector('[data-bundle-cta-wrap]');
      if (ctaWrap) this.ctaResizeObserver.observe(ctaWrap);
      window.addEventListener('resize', this.updateCtaOffset);

      // After tab morph: bundle-card elements may be replaced (childList) OR
      // morphed in place (attributes only — the theme's `@theme/morph` keeps
      // the existing DOM nodes when their tag names match, and just rewrites
      // attributes/children). Observe both signals so cross-tab selection
      // state survives tab switches:
      //   - childList: cards added/removed
      //   - attributes: card slot rewired to a different product
      //     (data-product-id changes) or server-side aria-pressed reset
      this.gridObserver = new MutationObserver(this.handleGridMutation.bind(this));
      this.gridObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-product-id', 'data-variants-count'],
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick, true);
      this.removeEventListener('click', this.handleQtyWrapClick, true);
      this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQtyChange);
      document.removeEventListener('click', this.handleDocumentClick, true);
      document.removeEventListener(DialogOpenEvent.eventName, this.handleDialogOpen);
      document.removeEventListener(DialogCloseEvent.eventName, this.handleDialogClose);
      window.removeEventListener('resize', this.updateCtaOffset);
      this.gridObserver?.disconnect();
      this.ctaResizeObserver?.disconnect();
      document.body.style.removeProperty('--bundle-cta-height');
    }

    handleDialogOpen(event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var ctaWrap = this.querySelector('[data-bundle-cta-wrap]');
      if (ctaWrap && target.closest('cart-drawer-component')) {
        ctaWrap.dataset.drawerOpen = 'true';
      }

      // When the quick-add modal opens from a bundle card, cap its qty input
      // at the remaining bundle headroom so the user can't pick more than the
      // bundle's max allows. We clamp again at submit time as a safety net.
      if (this.pendingCard) {
        var dialog = document.getElementById('quick-add-dialog');
        if (dialog && (dialog === target || dialog.contains(target))) {
          this.applyModalQtyCap(dialog);
        }
      }
    }

    applyModalQtyCap(dialog) {
      var productId = this.pendingCard && this.pendingCard.dataset.productId;
      var headroom = this.remainingHeadroom(productId);
      if (headroom < 1) headroom = 1;
      // Modal renders its form async — give the DOM a tick to settle, then
      // patch the qty input + nudge the component to recompute disabled state.
      var self = this;
      requestAnimationFrame(function () {
        var input = dialog.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]');
        if (!input) return;
        input.setAttribute('data-cart-quantity', '0');
        input.max = String(headroom);
        if (parseInt(input.value, 10) > headroom) input.value = String(headroom);
        var component = input.closest('quantity-selector-component');
        if (component && typeof component.updateButtonStates === 'function') {
          component.updateButtonStates();
        }
        // If the cap dropped to 1, also disable the +1 button up-front.
        if (headroom === 1) {
          var plus = component && component.querySelector('.quantity-plus');
          if (plus) plus.disabled = true;
        }
        self.modalQtyCapped = true;
      });
    }

    handleDialogClose(event) {
      var ctaWrap = this.querySelector('[data-bundle-cta-wrap]');
      if (!ctaWrap) return;
      var target = event.target;
      if (target && target.closest && target.closest('cart-drawer-component')) {
        delete ctaWrap.dataset.drawerOpen;
      }
    }

    updateCtaOffset() {
      var ctaWrap = this.querySelector('[data-bundle-cta-wrap]');
      if (!ctaWrap || ctaWrap.hidden) {
        document.body.style.removeProperty('--bundle-cta-height');
        return;
      }
      var h = ctaWrap.getBoundingClientRect().height;
      document.body.style.setProperty('--bundle-cta-height', h + 'px');
    }

    // Helpers to mark / unmark the quick-add dialog as in bundle context.
    // (No clearBundleModalContext call on manual modal close — it's only
    // needed to suppress the bundle CTA label, and the modal is only ever
    // opened from bundle cards on this page.)
    setBundleModalContext() {
      var dialog = this.modalDialog || document.getElementById('quick-add-dialog');
      if (dialog) dialog.classList.add('is-bundle-context');
    }

    handleGridMutation(mutations) {
      var needsRefresh = mutations.some(function (m) {
        // Card added/removed.
        if (m.type === 'childList') {
          for (var i = 0; i < m.addedNodes.length; i++) {
            var n = m.addedNodes[i];
            if (n.nodeType !== 1) continue;
            if (n.matches && n.matches('.bundle-card')) return true;
            if (n.querySelector && n.querySelector('.bundle-card')) return true;
          }
        }
        // Card slot rewired to a different product (morph-in-place rewrites
        // data-product-id / data-variants-count attributes).
        if (m.type === 'attributes' && m.target instanceof Element) {
          if (m.target.classList && m.target.classList.contains('bundle-card')) return true;
        }
        return false;
      });
      if (!needsRefresh) return;
      this.applySelectionState();
      this.renderProgress();
      this.relabelCardButtons();
    }

    // Per-card setup (no text manipulation — CSS handles labels via ::after):
    //   - Force data-quick-add-button="add" on single/no-variant cards so the
    //     "Add" button is visible (the snippet's default logic incorrectly
    //     renders "Choose" when no swatch is preselected).
    //   - Suppress fly-to-cart animation in bundle context.
    //   - Strip the on:click attribute on the --choose button while selected
    //     so a click can't re-open the quick-add modal — the document-level
    //     delegation registered by @theme/component (component.js:223) bails
    //     out when the attribute is missing.
    relabelCardButtons() {
      this.querySelectorAll('.bundle-card').forEach(function (card) {
        var variantsCount = parseInt(card.dataset.variantsCount, 10) || 1;
        var qac = card.querySelector('quick-add-component');
        var isSelected = card.getAttribute('aria-pressed') === 'true';

        if (variantsCount <= 1 && qac && qac.getAttribute('data-quick-add-button') !== 'add') {
          qac.setAttribute('data-quick-add-button', 'add');
        }

        card.querySelectorAll('add-to-cart-component').forEach(function (atc) {
          if (atc.getAttribute('data-add-to-cart-animation') !== 'false') {
            atc.setAttribute('data-add-to-cart-animation', 'false');
          }
        });

        if (variantsCount > 1) {
          var chooseBtn = card.querySelector('.quick-add__button--choose');
          if (chooseBtn) {
            if (isSelected) {
              if (chooseBtn.hasAttribute('on:click')) {
                chooseBtn.dataset.bundleOnClick = chooseBtn.getAttribute('on:click') || '';
                chooseBtn.removeAttribute('on:click');
              }
            } else {
              var saved = chooseBtn.dataset.bundleOnClick || 'quick-add-component/handleClick';
              if (chooseBtn.getAttribute('on:click') !== saved) {
                chooseBtn.setAttribute('on:click', saved);
              }
            }
          }
        }
      });
    }

    readCopyTemplates() {
      var script = this.querySelector('script[type="application/json"][data-bundle-copy]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent || '{}');
      } catch (e) {
        return {};
      }
    }

    interpolate(template, vars) {
      if (!template) return '';
      return template
        .replace(/\[remaining\]/g, vars.remaining)
        .replace(/\[min\]/g, vars.min)
        .replace(/\[discount\]/g, this.discountLabel || '');
    }

    // ---- Click routing -------------------------------------------------------

    handleClick(event) {
      // Tabs are handled by the inline <script type="module"> in the section.
      if (event.target.closest('[data-collection-handle]')) return;

      // Quantity selector lives inside the card — clicks on +/-/input must
      // never reach the bare-card toggle logic below. Bail out before any
      // other branch runs. (handleQtyWrapClick also stopPropagation()s the
      // event, but both listeners are capture-phase on the same element and
      // handleClick was registered first, so we'd otherwise toggle the card
      // before the qty handler runs.)
      if (event.target.closest('[data-bundle-qty-wrap]')) return;

      // CTA
      if (event.target.closest('[data-bundle-cta]')) {
        event.preventDefault();
        this.handleAddBundleToCart();
        return;
      }

      // Sold-out product → block any interaction. CSS also pointer-events:
      // none, but this is defense-in-depth.
      var unavailableCard = event.target.closest('.bundle-card[data-available="false"]');
      if (unavailableCard && this.contains(unavailableCard)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // "Add to Bundle" / "Remove from Bundle" — single-variant card button.
      // preventDefault on the click cancels the form submission (button is
      // type="submit") at its source — no cart fetch, no drawer open.
      // AddToCartComponent's animateAddToCart still runs (it's a separate
      // listener on the same click event).
      var addBtn = event.target.closest('.bundle-card .quick-add__button--add');
      if (addBtn) {
        var card = addBtn.closest('.bundle-card');
        if (!card || !this.contains(card)) return;
        if (card.getAttribute('aria-disabled') === 'true' &&
            card.getAttribute('aria-pressed') !== 'true') {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        var wasSelected = card.getAttribute('aria-pressed') === 'true';
        if (wasSelected) {
          this.deselect(card);
        } else {
          this.select(card, card.dataset.defaultVariantId);
          // Fire the theme's checkmark-burst animation manually since we
          // cancelled the click's default action. Same call product-form
          // component makes after a successful add (`product-form.js`
          // handleSubmit line 294).
          var atc = addBtn.closest('add-to-cart-component');
          if (atc && typeof atc.animateAddToCart === 'function') {
            atc.animateAddToCart();
          }
        }
        return;
      }

      // "Select options" / "Remove from Bundle" — multi-variant card button.
      var chooseBtn = event.target.closest('.bundle-card .quick-add__button--choose');
      if (chooseBtn) {
        var mcard = chooseBtn.closest('.bundle-card');
        if (!mcard || !this.contains(mcard)) return;
        if (mcard.getAttribute('aria-pressed') === 'true') {
          // Already in the bundle — clicking the button removes it. The
          // `on:click` attribute has been stripped by relabelCardButtons() so
          // the modal cannot open; preventDefault stops form submission too.
          event.preventDefault();
          this.deselect(mcard);
          return;
        }
        if (mcard.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        // Not selected — let the native quick-add modal open. Mark pending and
        // tag the dialog so the CSS label override kicks in from frame 1.
        this.pendingCard = mcard;
        document.body.dataset.bundlePendingCard = mcard.dataset.productId;
        this.setBundleModalContext();
        return;
      }

      // Bare card click (image, title, anywhere else inside .bundle-card) →
      // toggle selection. Block PDP navigation entirely on the bundle page.
      //
      // We need BOTH preventDefault (cancels the anchor's default navigation
      // for clicks on the `<a>` inside the card) AND stopPropagation (stops
      // the event from reaching <product-card>'s bubble-phase navigateToProduct
      // handler, which would otherwise call window.location.href = … for any
      // click outside an interactive element).
      var bareCard = event.target.closest('.bundle-card');
      if (bareCard && this.contains(bareCard)) {
        event.preventDefault();
        event.stopPropagation();
        if (bareCard.getAttribute('aria-disabled') === 'true' &&
            bareCard.getAttribute('aria-pressed') !== 'true') {
          return;
        }
        if (bareCard.getAttribute('aria-pressed') === 'true') {
          this.deselect(bareCard);
          return;
        }
        var bcVariants = parseInt(bareCard.dataset.variantsCount, 10) || 1;
        if (bcVariants > 1) {
          // Multi-variant — programmatically click the choose button to open
          // the modal (handler chain takes over from there).
          var choose = bareCard.querySelector('.quick-add__button--choose');
          if (choose instanceof HTMLElement) {
            this.pendingCard = bareCard;
            document.body.dataset.bundlePendingCard = bareCard.dataset.productId;
            this.setBundleModalContext();
            choose.click();
          }
          return;
        }
        // Single-variant — select with the default variant and fire the
        // checkmark animation on the in-card add-to-cart button.
        this.select(bareCard, bareCard.dataset.defaultVariantId);
        var bcAtc = bareCard.querySelector('add-to-cart-component');
        if (bcAtc && typeof bcAtc.animateAddToCart === 'function') {
          bcAtc.animateAddToCart();
        }
      }
    }

    // Best-effort: read the currently-displayed variant price from the quick-add
    // modal form. Quick-add renders a price element with [data-product-price] or
    // [data-price] in cents — we look up the nearest one. If not found, returns
    // null and the caller falls back to the card's default variant price.
    readVariantPriceFromForm(form) {
      if (!form) return null;
      var scope = form.closest('dialog, form, product-form-component') || form;
      var el = scope.querySelector('[data-product-price][data-price-cents], [data-price-cents]');
      if (el && el.dataset.priceCents) return parseInt(el.dataset.priceCents, 10);
      return null;
    }

    // Read the user-picked quantity from the quick-add modal. The qty input
    // is inside the product form, but the form's structure varies — some
    // themes hoist the qty selector outside the <form> and bind it via
    // `form="form-id"`. Search the form first, then fall back to the
    // surrounding dialog / product-form-component so we don't silently
    // default to qty=1 when the user picked 6.
    readQuantityFromForm(form) {
      var input = null;
      if (form) input = form.querySelector('input[name="quantity"]');
      if (!input && form) {
        var scope = form.closest('dialog, product-form-component, quick-add-dialog');
        if (scope) input = scope.querySelector('input[name="quantity"]');
      }
      if (!input) {
        var dialog = document.getElementById('quick-add-dialog');
        if (dialog) input = dialog.querySelector('input[name="quantity"]');
      }
      if (!input) return null;
      var n = parseInt(input.value, 10);
      if (isNaN(n) || n < 1) return 1;
      return n;
    }

    // Document-level capture click — intercepts the quick-add modal's submit
    // button when bundle context is active.
    handleDocumentClick(event) {
      if (!this.pendingCard) return;
      var dialog = document.getElementById('quick-add-dialog');
      if (!dialog || !dialog.contains(event.target)) return;
      var btn = event.target.closest('button[name="add"]');
      if (!btn) return;

      // preventDefault on the click cancels the type="submit" form submission.
      // stopImmediatePropagation prevents AddToCartComponent.handleClick from
      // running fly-to-cart animation — we don't want it for bundle adds.
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      var form = btn.closest('form');
      var variantInput = form && form.querySelector('input[name="id"]');
      var variantId = (variantInput && variantInput.value) || this.pendingCard.dataset.defaultVariantId;
      var variantPrice = this.readVariantPriceFromForm(form);
      var modalQty = this.readQuantityFromForm(form);
      if (variantId) this.select(this.pendingCard, variantId, variantPrice, modalQty);

      // Trigger the theme's checkmark animation on the in-card button so the
      // user sees feedback after closing the modal.
      var cardAtc = this.pendingCard.querySelector('add-to-cart-component');
      if (cardAtc && typeof cardAtc.animateAddToCart === 'function') {
        cardAtc.animateAddToCart();
      }

      this.pendingCard = null;
      delete document.body.dataset.bundlePendingCard;

      if (typeof dialog.closeDialog === 'function') dialog.closeDialog();
    }

    // ---- Selection state ----------------------------------------------------

    // Total quantity across all selected products. Replaces selected.size
    // wherever we used to count "items in the bundle" — a single product with
    // qty=4 now counts as 4 toward min/max.
    totalQuantity() {
      var total = 0;
      this.state.selected.forEach(function (entry) {
        total += parseInt(entry.qty, 10) || 1;
      });
      return total;
    }

    // Remaining capacity in the bundle, excluding `excludeProductId`'s own qty.
    // Used to clamp modal-submitted qty values and to set per-card max caps.
    remainingHeadroom(excludeProductId) {
      var others = 0;
      this.state.selected.forEach(function (entry, id) {
        if (id === excludeProductId) return;
        others += parseInt(entry.qty, 10) || 1;
      });
      return Math.max(0, this.maxProducts - others);
    }

    select(card, variantId, variantPrice, requestedQty) {
      if (!card || !variantId) return;
      var productId = card.dataset.productId;
      var selected = this.state.selected;
      // Block new selections (not re-selects of an already-picked product)
      // when total quantity has hit the bundle cap.
      if (this.totalQuantity() >= this.maxProducts && !selected.has(productId)) return;

      // Fall back to the card's default variant price when no explicit price
      // is provided (covers most stores where variants share the same price).
      var priceCents = parseInt(variantPrice, 10);
      if (isNaN(priceCents)) {
        priceCents = parseInt(card.dataset.defaultVariantPrice, 10) || 0;
      }

      // Resolve quantity: explicit > preserved existing > 1. Clamp to the
      // remaining bundle headroom so the user can't sneak past max_products
      // via the modal qty input.
      var existing = selected.get(productId);
      var qty;
      if (requestedQty != null) {
        qty = parseInt(requestedQty, 10);
        if (isNaN(qty) || qty < 1) qty = 1;
      } else {
        qty = (existing && existing.qty) || 1;
      }
      var cap = this.remainingHeadroom(productId);
      if (qty > cap) qty = cap;
      if (qty < 1) qty = 1;

      selected.set(productId, {
        variantId: String(variantId),
        title: card.dataset.productTitle || '',
        handle: card.dataset.productHandle || '',
        price: priceCents,
        qty: qty,
      });
      card.setAttribute('aria-pressed', 'true');
      this.applySelectionState();
      this.renderProgress();
      this.relabelCardButtons();
    }

    // ---- Quantity selector wiring ------------------------------------------

    // Fired by the theme's quantity-selector-component on every +/-/blur.
    // Event detail: { quantity, cartLine }. We ignore cartLine here.
    handleQtyChange(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var wrap = target.closest('[data-bundle-qty-wrap]');
      if (!wrap) return;
      var card = wrap.closest('.bundle-card');
      if (!card || !this.contains(card)) return;

      var productId = card.dataset.productId;
      var entry = this.state.selected.get(productId);
      if (!entry) return;

      var newQty = parseInt(event.detail && event.detail.quantity, 10);
      if (isNaN(newQty) || newQty < 1) newQty = 1;
      entry.qty = newQty;

      this.applySelectionState();
      this.renderProgress();
    }

    // Capture-phase click handler. Stops clicks on the qty selector from
    // bubbling into the bundle card's bare-click handler (which would toggle
    // the selection state).
    handleQtyWrapClick(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-bundle-qty-wrap]')) {
        event.stopPropagation();
      }
    }

    deselect(card) {
      var productId = card.dataset.productId;
      this.state.selected.delete(productId);
      card.setAttribute('aria-pressed', 'false');
      this.applySelectionState();
      this.renderProgress();
      this.relabelCardButtons();
    }

    applySelectionState() {
      var total = this.totalQuantity();
      var atMax = total >= this.maxProducts;
      var self = this;
      this.querySelectorAll('.bundle-card').forEach(function (card) {
        var id = card.dataset.productId;
        var isSelected = self.state.selected.has(id);
        card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        if (atMax && !isSelected) card.setAttribute('aria-disabled', 'true');
        else card.removeAttribute('aria-disabled');

        // Sync the per-card qty input (single-variant cards only) with our
        // state, and cap its `max` at the remaining bundle headroom so the
        // theme's quantity-selector-component disables the `+` button
        // automatically once the bundle is full.
        self.syncCardQty(card, isSelected, total);
      });
    }

    // Sync one card's qty input/value/max with bundle state. Called from
    // applySelectionState for every card. No-op for multi-variant cards
    // (they don't render a qty wrapper).
    syncCardQty(card, isSelected, totalQty) {
      var wrap = card.querySelector('[data-bundle-qty-wrap]');
      if (!wrap) return;
      var input = wrap.querySelector('input[name="quantity"]');
      if (!input) return;

      // The shared snippet's component subtracts data-cart-quantity from max
      // to derive its effective max (PDP "how many can be added" semantics).
      // In bundle context the cart count is irrelevant — always treat it as 0.
      if (input.getAttribute('data-cart-quantity') !== '0') {
        input.setAttribute('data-cart-quantity', '0');
      }

      var entry = isSelected ? this.state.selected.get(card.dataset.productId) : null;
      var qty = entry ? (entry.qty || 1) : 1;

      // Headroom = max - (everyone else's quantity). When the card isn't
      // selected, headroom uses the full max minus current total.
      var others = totalQty - (entry ? qty : 0);
      var headroom = Math.max(1, this.maxProducts - others);

      if (input.max !== String(headroom)) input.max = String(headroom);
      if (input.value !== String(qty)) input.value = String(qty);

      // Nudge the component to recompute button-disabled states with the
      // fresh max/value. updateButtonStates is the lightest public-ish hook.
      var component = wrap.querySelector('quantity-selector-component');
      if (component && typeof component.updateButtonStates === 'function') {
        component.updateButtonStates();
      }

      wrap.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
    }

    renderProgress() {
      var count = this.totalQuantity();
      var min = this.minProducts;
      var remaining = Math.max(0, min - count);

      // 4 states. `progress` is the new "items to go" middle state matching
      // Figma — count > 0 and more than one item still needed.
      var stateName = 'default';
      if (count >= min) stateName = 'unlocked';
      else if (remaining === 1) stateName = 'almost';
      else if (count > 0) stateName = 'progress';

      this.setAttribute('data-state', stateName);
      this.setAttribute('data-selected-count', String(count));

      var headingEl = this.querySelector('[data-bundle-heading]');
      var bodyEl = this.querySelector('[data-bundle-body]');
      var headingTpl = this.copyTemplates['heading_' + stateName] || '';
      var bodyTpl = this.copyTemplates['body_' + stateName] || '';
      var vars = { remaining: remaining, min: min };
      if (headingEl) headingEl.textContent = this.interpolate(headingTpl, vars);
      if (bodyEl) bodyEl.innerHTML = this.interpolate(bodyTpl, vars);

      var countEl = this.querySelector('[data-bundle-count]');
      if (countEl) countEl.textContent = count + ' selected';

      // Render fill on both the progress widget bar and the sticky CTA bar
      // (CSS selector matches both since they share the same hook).
      var pct = Math.min(100, (count / min) * 100);
      this.querySelectorAll('[data-bundle-bar-fill]').forEach(function (fill) {
        fill.style.width = pct + '%';
      });
      var bar = this.querySelector('.bundle-progress__bar');
      if (bar) bar.setAttribute('aria-valuenow', String(Math.min(count, min)));

      this.renderStickyCta(count, remaining, stateName);
    }

    // ---- Sticky CTA rendering ----------------------------------------------

    renderStickyCta(count, remaining, stateName) {
      var ctaWrap = this.querySelector('[data-bundle-cta-wrap]');
      var cta = this.querySelector('[data-bundle-cta]');
      if (!ctaWrap || !cta) return;

      // Visible from 1 selected onward (NOT just when min reached).
      var shouldShow = count >= 1;
      ctaWrap.hidden = !shouldShow;

      var unlocked = stateName === 'unlocked';
      cta.disabled = !unlocked;
      cta.setAttribute('aria-disabled', unlocked ? 'false' : 'true');

      // Label: "Select N More" (disabled) → "Add Bundle To Cart" (unlocked).
      var labelEl = this.querySelector('[data-bundle-cta-label]');
      if (labelEl) {
        var tpl = unlocked
          ? (this.copyTemplates.cta_add_bundle || 'Add Bundle To Cart')
          : (this.copyTemplates.cta_select_more || 'Select [remaining] More');
        labelEl.textContent = this.interpolate(tpl, { remaining: remaining, min: this.minProducts });
      }

      // "N item(s) selected" copy.
      var countEl = this.querySelector('[data-bundle-cta-count]');
      if (countEl) {
        var countTplKey = count === 1 ? 'items_selected_singular' : 'items_selected_plural';
        var countTpl = this.copyTemplates[countTplKey] || (count + ' selected');
        countEl.textContent = countTpl.replace(/\[count\]/g, String(count));
      }

      // Subtotal: sum of (variant price × qty) for each selected product.
      var subtotalCents = 0;
      this.state.selected.forEach(function (entry) {
        var price = parseInt(entry.price, 10) || 0;
        var qty = parseInt(entry.qty, 10) || 1;
        subtotalCents += price * qty;
      });

      var priceEl = this.querySelector('[data-bundle-cta-price]');
      var originalEl = this.querySelector('[data-bundle-cta-original-price]');
      var discountedEl = this.querySelector('[data-bundle-cta-discounted-price]');
      var savePill = this.querySelector('[data-bundle-cta-save-pill]');

      if (unlocked && this.discountPercent > 0) {
        // Show line-through original + discounted + save pill.
        var discountedCents = Math.round(subtotalCents * (1 - this.discountPercent / 100));
        if (priceEl) priceEl.hidden = true;
        if (originalEl) {
          originalEl.hidden = false;
          originalEl.textContent = this.formatMoney(subtotalCents);
        }
        if (discountedEl) {
          discountedEl.hidden = false;
          discountedEl.textContent = this.formatMoney(discountedCents);
        }
        if (savePill) savePill.hidden = false;
      } else {
        // Plain single subtotal.
        if (priceEl) {
          priceEl.hidden = false;
          priceEl.textContent = this.formatMoney(subtotalCents);
        }
        if (originalEl) originalEl.hidden = true;
        if (discountedEl) discountedEl.hidden = true;
        if (savePill) savePill.hidden = true;
      }

      // Re-measure CTA height so body padding tracks state changes.
      this.updateCtaOffset();
    }

    // Parse two Liquid-rendered probes:
    //   moneyZero   = "{{ 0 | money }}"        e.g. "$0.00"  → prefix/suffix/decimal
    //   moneySample = "{{ 1234567 | money }}"  e.g. "$12,345.67" → thousand sep
    // Returns a spec describing exactly how the theme's money filter formats
    // numbers in this currency. Used by formatMoney() to render byte-identical
    // output without re-implementing the filter rules.
    parseMoneyFormat(moneyZero, moneySample) {
      var spec = { prefix: '$', suffix: '', thousand: ',', decimal: '.', decimals: 2 };
      // Find the digits in the zero sample: prefix / decimal block / suffix.
      var zeroMatch = /^(\D*)(\d+)(\D)?(\d*)(.*)$/.exec(moneyZero);
      if (zeroMatch) {
        spec.prefix = zeroMatch[1] || '';
        if (zeroMatch[3] && zeroMatch[4]) {
          spec.decimal = zeroMatch[3];
          spec.decimals = zeroMatch[4].length;
        } else {
          spec.decimals = 0;
          spec.decimal = '';
        }
        spec.suffix = zeroMatch[5] || '';
      }
      // Find the thousand separator in the sample (the non-digit char between
      // the leading digits — e.g. between "12" and "345" in "$12,345.67").
      var digits = moneySample.replace(spec.prefix, '').replace(spec.suffix, '');
      var thouMatch = /\d+(\D)\d{3}/.exec(digits);
      if (thouMatch) spec.thousand = thouMatch[1];
      return spec;
    }

    // Money formatter — uses the Liquid-derived format spec so output matches
    // what `{{ X | money }}` would render (e.g. "$5,487.63", "1.234,56 €").
    formatMoney(cents) {
      var spec = this.moneyFormatSpec;
      var value = Math.abs(cents) / 100;
      var fixed = value.toFixed(spec.decimals);
      var parts = fixed.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, spec.thousand);
      var decPart = parts[1] ? spec.decimal + parts[1] : '';
      var sign = cents < 0 ? '-' : '';
      return spec.prefix + sign + intPart + decPart + spec.suffix;
    }

    handleFormSubmit(event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;

      var dialog = document.getElementById('quick-add-dialog');
      var inDialog = dialog && dialog.contains(form);
      var inBundleCard = form.closest('.bundle-card');
      var ownsBundleCard = inBundleCard && this.contains(inBundleCard);

      // Path A: multi-variant — modal launched from a bundle card, user picked
      // a variant and submitted.
      if (inDialog && this.pendingCard) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();

        var modalVariant = form.querySelector('input[name="id"]');
        var modalVariantId = modalVariant && modalVariant.value;
        var modalVariantPrice = this.readVariantPriceFromForm(form);
        var modalQty = this.readQuantityFromForm(form);
        if (modalVariantId) this.select(this.pendingCard, modalVariantId, modalVariantPrice, modalQty);

        this.pendingCard = null;
        delete document.body.dataset.bundlePendingCard;

        if (typeof dialog.closeDialog === 'function') dialog.closeDialog();
        return;
      }

      // Path B: single-variant — the in-card "Add to Bundle" button submitted
      // its form directly (no modal). Convert to a bundle selection.
      if (ownsBundleCard && !inDialog) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();

        var cardVariant = form.querySelector('input[name="id"]');
        var cardVariantId = (cardVariant && cardVariant.value) || inBundleCard.dataset.defaultVariantId;
        if (cardVariantId) this.select(inBundleCard, cardVariantId);
      }
    }

    // ---- Add bundle to cart -------------------------------------------------

    async handleAddBundleToCart() {
      if (this.totalQuantity() < this.minProducts) return;
      var cta = this.querySelector('[data-bundle-cta]');
      if (cta) {
        cta.disabled = true;
        cta.setAttribute('aria-busy', 'true');
      }

      try {
        var items = Array.from(this.state.selected.values()).map(function (entry) {
          return {
            id: Number(entry.variantId),
            quantity: parseInt(entry.qty, 10) || 1,
            properties: (function () { var p = {}; p[BUNDLE_PROPERTY] = 'true'; return p; })(),
          };
        });

        var sectionIds = collectCartSectionIds();

        // Batch add — single /cart/add.js call.
        var addResp = await fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: items,
            ...(sectionIds.length && { sections: sectionIds.join(',') }),
          }),
        });
        if (!addResp.ok) {
          // Shopify returns a JSON body with a `description` (e.g. "The maximum
          // quantity of this item is already in your cart.") on 422. Surface it
          // on the CTA so the user understands why the add failed.
          var errBody = null;
          try { errBody = await addResp.json(); } catch (_) {}
          var msg = (errBody && (errBody.description || errBody.message))
            || 'Could not add bundle to cart (' + addResp.status + ').';
          var labelEl = this.querySelector('[data-bundle-cta-label]');
          if (labelEl) labelEl.textContent = msg;
          return;
        }
        var cart = await addResp.json();

        // Apply discount code, merging with existing codes.
        //
        // /cart/add.js does not reliably include `discount_codes` in its
        // response, so explicitly fetch /cart.js when the field is missing —
        // otherwise /cart/update.js below would replace existing codes (e.g.
        // UPSELL30 from a prior upsell add) with just BUNDLE15, dropping the
        // upsell discount.
        if (this.discountCode) {
          var existing = readDiscountCodes(cart);
          if (!Array.isArray(cart.discount_codes)) {
            try {
              var currentCartResp = await fetch(ROOT + 'cart.js', {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
              });
              if (currentCartResp.ok) {
                existing = readDiscountCodes(await currentCartResp.json());
              }
            } catch (e) {
              console.warn('[bundle-builder] read existing discount codes failed:', e);
            }
          }

          var merged = existing.indexOf(this.discountCode) >= 0
            ? existing
            : existing.concat([this.discountCode]);
          try {
            var updateResp = await fetch(ROOT + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({
                discount: merged.join(','),
                ...(sectionIds.length && { sections: sectionIds.join(',') }),
              }),
            });
            if (updateResp.ok) cart = await updateResp.json();
          } catch (e) {
            console.warn('[bundle-builder] discount apply failed:', e);
          }
        }

        // Dispatch the theme's typed CartAddEvent so the cart drawer
        // re-renders its contents from cart.sections. Without the typed
        // event (and the sections payload), the drawer opens but stays
        // empty — same pattern as upsell-add-to-cart.js.
        this.dispatchEvent(
          new CartAddEvent(cart, this.id || 'bundle-builder', {
            source: 'bundle-builder',
            itemCount: items.length,
            sections: cart.sections || {},
          })
        );

        // Reset bundle state. relabelCardButtons() is critical here — any
        // previously-selected multi-variant card had its on:click stripped
        // (see relabelCardButtons), so without restoring it the choose button
        // would no longer open the quick-add modal on the next bundle.
        this.state.selected.clear();
        this.pendingCard = null;
        delete document.body.dataset.bundlePendingCard;
        this.applySelectionState();
        this.relabelCardButtons();
        this.renderProgress();

        // Re-render the entire bundle section from the server. This refreshes
        // product availability (stock pulled down by the add) and resets the
        // tab nav to the default collection, giving the user a clean slate to
        // build another bundle without a full page reload.
        //
        // The bundle section is template-scoped to the COLLECTION template
        // (section_id is template--<collection-template-id>__bundle_builder),
        // so we must render against a /collections/<handle> URL — rendering at
        // the current page URL returns a response that doesn't contain the
        // section ID. We use the active tab's handle (falling back to the first
        // tab) so the rerender reflects the active filter.
        //
        // The MutationObserver on the grid will fire after the morph and
        // reapply selection state + relabelCardButtons against the fresh DOM.
        try {
          var activeTab = this.querySelector('.bundle-builder__tab--active[data-collection-handle]')
            || this.querySelector('[data-collection-handle]');
          if (activeTab) {
            var handle = activeTab.getAttribute('data-collection-handle');
            var renderUrl = new URL('/collections/' + handle, window.location.origin);
            await sectionRenderer.renderSection(this.sectionId, { url: renderUrl, cache: false });
          }
        } catch (renderErr) {
          console.warn('[bundle-builder] section re-render failed:', renderErr);
        }
      } catch (e) {
        console.error('[bundle-builder] add to cart failed:', e);
      } finally {
        if (cta) {
          cta.removeAttribute('aria-busy');
          // disabled state is re-derived by renderProgress() via state
        }
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
